import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { graphql } from 'gatsby';

import { propTypesFooter } from '../../../src/shared/components/Layout/Footer/Footer.proptypes';
import {
  propTypesLocalization,
  propTypesSite,
} from '../../../src/shared/constants/Proptypes';
import { propTypesNavigation } from '../../shared/components/Layout/Header/Header.proptypes';
import { propTypesHomepage } from '../Homepage/Homepage.proptypes';
import { propTypesPost } from './Post.proptypes';

import { BreadcrumbSection } from '../../shared/components/BreadcrumbSection';
import { Layout } from '../../shared/components/Layout';
import RecommendedGallery from '../../shared/components/RecommendedGallery';
import { Seo } from '../../shared/components/SEO';
import ShareSection from '../../shared/components/ShareSection';
import { PostFooter } from './components/PostFooter';
import { PostHeader } from './components/PostHeader';
import { SliceZonePost } from './components/SliceZonePost';

import { getTagList } from '../../../src/shared/utils/getTagList';

import { DocumentType } from '../../../src/shared/constants/Enums';
import * as S from './Post.styles';

const Post = ({ data, location }) => {
  if (!data) {
    return null;
  }

  const hits = location.state?.searchHits;
  const authorRef = useRef();
  const [isMenuVisible, setIsMenuVisible] = useState(true);

  const handleScroll = () => {
    const currentScrollPos =
      typeof window !== 'undefined' ? window.pageYOffset : 0;
    const authorPosition = authorRef.current?.offsetTop;

    if (currentScrollPos > authorPosition - 500) {
      setIsMenuVisible(false);
    } else {
      setIsMenuVisible(true);
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll, true);

      return () => window.removeEventListener('scroll', handleScroll, true);
    }
  });

  const {
    prismicPost: document,
    allPrismicPost: { edges: prismicAllPosts } = {},
    prismicHomepage,
    prismicLocalization,
    prismicNavigation,
    prismicFooter,
  } = data || {};

  const {
    data: post,
    lang,
    type,
    url,
    alternate_languages: alternateLanguages,
    uid,
  } = document || {};

  const {
    breadcrumb_title: breadcrumbTitle,
    title: { text: h1title } = {},
    author: { document: author } = {},
    meta_title: metaTitle,
    category,
    tag_1: tag1,
    tag_2: tag2,
    tag_3: tag3,
    body: slices,
    is_shareable: isShareable,
  } = post || '';

  const { data: authorData } = author || '';

  const activeDoc = {
    lang,
    type,
    url,
    alternateLanguages,
    uid,
  };

  const tagList = getTagList({
    lang,
    tag1,
    tag2,
    tag3,
  });

  const breadcrumbPageName = breadcrumbTitle || h1title || metaTitle || '';

  const parentUid = category?.uid || '';
  const parentName = category?.document?.data?.category_name || '';

  const breadcrumb = {
    parentName,
    parentUid,
    parentType: DocumentType.CATEGORY,
    parentLang: lang,
  };

  return (
    <Layout
      activeDocMeta={activeDoc}
      footer={prismicFooter}
      localization={prismicLocalization}
      navigation={prismicNavigation}
    >
      <BreadcrumbSection
        currentLanguage={lang}
        currentPageName={breadcrumbPageName}
        homepage={prismicHomepage}
        parentPage={breadcrumb}
        prismicDocumentType={DocumentType.POST}
      />
      <S.Container>
        <PostHeader localization={prismicLocalization} post={post} />
        <SliceZonePost localization={prismicLocalization} slices={slices} />
        <S.HorizontalLine ref={authorRef} />
        <PostFooter
          author={authorData}
          localization={prismicLocalization}
          tags={tagList}
        />
      </S.Container>
      <RecommendedGallery
        allPages={prismicAllPosts}
        documentType={DocumentType.POST}
        hits={hits}
        localization={prismicLocalization}
        page={document}
      />
      {isShareable && (
        <ShareSection
          isVisible={isMenuVisible}
          localization={prismicLocalization}
          page={post}
        />
      )}
    </Layout>
  );
};

export const Head = ({ data }) => {
  const {
    prismicPost: document,
    site: prismicSite,
    prismicHomepage,
  } = data || {};

  const { data: post } = document || {};

  const {
    block_search_indexing: hasNoRobots,
    title: { text: h1title } = {},
    meta_title: metaTitle,
    faq_content: FAQContent,
    reviews,
  } = post || '';

  const pageMetaTitle = metaTitle || h1title || '';

  const hasFAQ =
    FAQContent?.length >= 1
      ? FAQContent?.[0]?.answer !== null && FAQContent?.[0]?.question !== null
      : false;

  return (
    <Seo
      hasNoRobots={hasNoRobots}
      defaultDocument={prismicHomepage}
      document={document}
      FAQContent={FAQContent}
      hasFAQ={hasFAQ}
      isArticle={true}
      reviews={reviews}
      site={prismicSite}
      title={pageMetaTitle}
    />
  );
};

Post.propTypes = {
  /**
   * GraphQL query result.
   */
  data: PropTypes.shape({
    allPrismicPost: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape(propTypesPost),
        })
      ),
    }),
    prismicFooter: PropTypes.shape(propTypesNavigation),
    prismicHomepage: PropTypes.shape(propTypesHomepage),
    prismicLocalization: PropTypes.shape(propTypesLocalization),
    prismicNavigation: PropTypes.shape(propTypesFooter),
    prismicPost: PropTypes.shape(propTypesPost),
    site: PropTypes.shape(propTypesSite),
  }),

  /**
   * Gatsby Link location state
   * https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-link/#pass-state-as-props-to-the-linked-page
   */
  location: PropTypes.shape({
    state: PropTypes.shape({
      searchHits: PropTypes.arrayOf(
        PropTypes.shape({
          category: PropTypes.string,
          description: PropTypes.string,
          language: PropTypes.string,
          objectID: PropTypes.string,
          tag1: PropTypes.string,
          tag2: PropTypes.string,
          tag3: PropTypes.string,
          title: PropTypes.string,
          url: PropTypes.string,
        })
      ),
    }),
  }),
};

export const query = graphql`
  query Post($uid: String, $lang: String) {
    site {
      ...DataSite
    }
    prismicHomepage(lang: { eq: $lang }) {
      ...DataHomepage
    }
    prismicPost(uid: { eq: $uid }, lang: { eq: $lang }) {
      ...DataPost
    }
    prismicLocalization(lang: { eq: $lang }) {
      ...DataLocalization
    }
    prismicNavigation(lang: { eq: $lang }) {
      ...DataNavigation
    }
    prismicFooter(lang: { eq: $lang }) {
      ...DataFooter
    }
    allPrismicPost(
      filter: { lang: { eq: $lang } }
      sort: { first_publication_date: DESC }
    ) {
      edges {
        node {
          ...DataPost
        }
      }
    }
  }
`;

export default Post;
